/**
 * Analytics Services for Reedle Web
 *
 * Services:
 * - Amplitude: Product analytics with session replay
 * - PostHog: Product analytics with cross-domain tracking
 * - GA4: Marketing attribution
 * - Hotjar: Session replay & heatmaps
 * - Clarity: Free session replay
 *
 * User Identification:
 * - Call identifyUser() after successful login
 * - Call clearUserIdentity() on logout
 */

import { useState, useEffect } from 'react';
import Hotjar from '@hotjar/browser';
import Clarity from '@microsoft/clarity';
import posthog from 'posthog-js';
// [TEMPORARILY DISABLED] Amplitude
// import * as amplitude from '@amplitude/analytics-browser';
// import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';

// Configuration
const POSTHOG_KEY = import.meta.env.VITE_PUBLIC_POSTHOG_KEY;
const POSTHOG_HOST = import.meta.env.VITE_PUBLIC_POSTHOG_HOST || 'https://eu.i.posthog.com';
const GA_MEASUREMENT_ID = 'G-4F130ZVFHS'; // reedle-web specific
const HOTJAR_SITE_ID = 6617199; // reedle-web specific
const HOTJAR_VERSION = 6;
const CLARITY_PROJECT_ID = 'uyygc4mj05'; // reedle-web specific
// [TEMPORARILY DISABLED] Amplitude
// const AMPLITUDE_API_KEY = import.meta.env.VITE_AMPLITUDE_API_KEY;

// Initialization flags
let posthogInitialized = false;
let gaInitialized = false;
let hotjarInitialized = false;
let clarityInitialized = false;
// [TEMPORARILY DISABLED] Amplitude
// let amplitudeInitialized = false;

// ============================================
// Initialization Functions
// ============================================

/**
 * Initialize PostHog product analytics
 */
export function initPostHog() {
  if (typeof window === 'undefined' || posthogInitialized) return;

  if (!POSTHOG_KEY) {
    console.warn('[PostHog] Missing API key');
    return;
  }

  try {
    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      person_profiles: 'identified_only', // Only create profiles for identified users
      capture_pageview: true,
      capture_pageleave: true,
      autocapture: true,
    });
    posthogInitialized = true;
    // console.log('[PostHog] Initialized successfully');
  } catch (error) {
    console.error('[PostHog] Initialization failed:', error);
  }
}

/**
 * Initialize Google Analytics 4
 */
export function initGA4() {
  if (typeof window === 'undefined' || gaInitialized) return;

  try {
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    document.head.appendChild(script);

    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() {
      window.dataLayer.push(arguments);
    };
    window.gtag('js', new Date());
    window.gtag('config', GA_MEASUREMENT_ID, {
      send_page_view: true,
      anonymize_ip: true,
      allow_google_signals: true,
      allow_ad_personalization_signals: true,
    });

    gaInitialized = true;
    // console.log('[GA4] Initialized successfully');
  } catch (error) {
    console.error('[GA4] Initialization failed:', error);
  }
}

/**
 * Initialize Hotjar session recording
 */
export function initHotjar() {
  if (typeof window === 'undefined' || hotjarInitialized) return;

  try {
    Hotjar.init(HOTJAR_SITE_ID, HOTJAR_VERSION);
    hotjarInitialized = true;
    // console.log('[Hotjar] Initialized successfully');
  } catch (error) {
    console.error('[Hotjar] Initialization failed:', error);
  }
}

/**
 * Initialize Microsoft Clarity session recording
 */
export function initClarity() {
  if (typeof window === 'undefined' || clarityInitialized) return;

  try {
    Clarity.init(CLARITY_PROJECT_ID);
    clarityInitialized = true;
    // console.log('[Clarity] Initialized successfully');
  } catch (error) {
    console.error('[Clarity] Initialization failed:', error);
  }
}

// [TEMPORARILY DISABLED] Amplitude
// export function initAmplitude() {
//   if (typeof window === 'undefined' || amplitudeInitialized) return;
//   if (!AMPLITUDE_API_KEY) {
//     console.debug('[Amplitude] Missing API key, skipping init');
//     return;
//   }
//   try {
//     amplitude.add(sessionReplayPlugin({
//       sampleRate: 1,
//       privacyConfig: { defaultMaskLevel: 'light' },
//     }));
//     amplitude.init(AMPLITUDE_API_KEY, {
//       logLevel: amplitude.Types.LogLevel.None,
//       fetchRemoteConfig: true,
//       autocapture: {
//         attribution: true,
//         fileDownloads: true,
//         formInteractions: true,
//         pageViews: true,
//         sessions: true,
//         elementInteractions: true,
//         networkTracking: true,
//         webVitals: true,
//         frustrationInteractions: true,
//       },
//       serverZone: 'EU',
//     });
//     amplitudeInitialized = true;
//   } catch (error) {
//     console.error('[Amplitude] Initialization failed:', error);
//   }
// }
export function initAmplitude() {
  // Amplitude temporarily disabled
}

// [TEMPORARILY DISABLED] Amplitude
// export function getAmplitude() {
//   return amplitude;
// }
export function getAmplitude() {
  return null;
}

// ============================================
// User Identification
// ============================================

/**
 * Identify user across all analytics services
 * Call this after successful login/registration
 * @param {string} userId - Unique user ID
 * @param {object} userData - User properties (username, email, etc.)
 */
export function identifyUser(userId, userData = {}) {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    // Google Analytics 4
    if (gaInitialized && window.gtag) {
      window.gtag('set', 'user_properties', {
        user_id: userId,
        username: userData.username,
      });
      // console.log('[GA4] User identified:', userId);
    }

    // [TEMPORARILY DISABLED] Amplitude
    // if (amplitudeInitialized) {
    //   amplitude.setUserId(userId);
    //   const identify = new amplitude.Identify();
    //   if (userData.email) identify.set('email', userData.email);
    //   if (userData.username) identify.set('username', userData.username);
    //   amplitude.identify(identify);
    // }

    // PostHog
    if (posthogInitialized) {
      posthog.identify(userId, {
        username: userData.username,
        email: userData.email,
      });
      // console.log('[PostHog] User identified:', userId);
    }

    // Clarity
    if (clarityInitialized && window.clarity) {
      window.clarity('identify', userId);
      // console.log('[Clarity] User identified:', userId);
    }

    // Hotjar
    if (hotjarInitialized && window.hj) {
      window.hj('identify', userId, {
        username: userData.username,
        email: userData.email,
      });
      // console.log('[Hotjar] User identified:', userId);
    }
  } catch (error) {
    console.error('[Analytics] User identification failed:', error);
  }
}

/**
 * Clear user identity on logout
 * Resets analytics to anonymous tracking
 */
export function clearUserIdentity() {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    // [TEMPORARILY DISABLED] Amplitude
    // if (amplitudeInitialized) {
    //   amplitude.reset();
    // }

    // PostHog - reset to anonymous
    if (posthogInitialized) {
      posthog.reset();
      // console.log('[PostHog] User identity cleared');
    }

    // Reset user counters
    localStorage.removeItem('reedle_user_counters');

    // Clear session tracking
    localStorage.removeItem('reedle_last_session_at');
    localStorage.removeItem('reedle_session_number');

    // Clarity - no explicit reset API
    // Hotjar - no explicit reset API
    // GA4 - no explicit reset API

    // console.log('[Analytics] User identity cleared');
  } catch (error) {
    console.error('[Analytics] Failed to clear user identity:', error);
  }
}

// ============================================
// Event Tracking
// ============================================

/**
 * Track custom event across all analytics platforms
 * @param {string} eventName - Name of the event
 * @param {object} properties - Event properties
 */
export function trackEvent(eventName, properties = {}) {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    // GA4
    if (gaInitialized && window.gtag) {
      window.gtag('event', eventName, properties);
    }

    // [TEMPORARILY DISABLED] Amplitude
    // if (amplitudeInitialized) {
    //   amplitude.track(eventName, properties);
    // }

    // PostHog
    if (posthogInitialized) {
      posthog.capture(eventName, properties);
    }
  } catch (error) {
    console.error('[Analytics] Failed to track event:', error);
  }
}

/**
 * Track SPA route changes
 * Call this on React Router navigation
 * @param {string} url - New URL path
 */
export function trackPageView(url) {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    // Hotjar's stateChange API for SPA navigation
    if (hotjarInitialized && window.hj) {
      window.hj('stateChange', url);
    }
  } catch (error) {
    console.error('[Hotjar] Page view tracking failed:', error);
  }
}

// ============================================
// Cross-Domain Tracking
// ============================================

/**
 * Capture cross-domain tracking parameters from URL hash
 * Call this on app initialization to continue tracking from reedle-site
 */
export function captureCrossDomainParams() {
  if (!posthogInitialized || typeof window === 'undefined') {
    return;
  }

  try {
    const hash = window.location.hash;
    if (!hash) return;

    const params = new URLSearchParams(hash.substring(1));
    const distinctId = params.get('ph_distinct_id');
    const sessionId = params.get('ph_session_id');

    if (distinctId && sessionId) {
      // Set the distinct_id and session_id from landing page
      posthog.identify(distinctId);
      posthog.set_config({ session_id: sessionId });

      // console.log('[PostHog] Cross-domain tracking captured:', { distinctId, sessionId });

      // Clean up URL hash
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
  } catch (error) {
    console.error('[PostHog] Failed to capture cross-domain params:', error);
  }
}

/**
 * Generate cross-domain URL with PostHog tracking params
 * Use this when linking from reedle-site to reedle-web
 * @param {string} url - Target URL
 * @returns {string} URL with tracking params
 */
export function getCrossDomainUrl(url) {
  if (!posthogInitialized || typeof window === 'undefined') {
    return url;
  }

  try {
    const distinctId = posthog.get_distinct_id();
    const sessionId = posthog.get_session_id();

    if (distinctId && sessionId) {
      const separator = url.includes('#') ? '&' : '#';
      return `${url}${separator}ph_distinct_id=${distinctId}&ph_session_id=${sessionId}`;
    }
  } catch (error) {
    console.error('[PostHog] Failed to generate cross-domain URL:', error);
  }

  return url;
}

// ============================================
// Utility Functions
// ============================================

/**
 * Update super properties (sent with every event)
 * Use this for frequently changing context
 * @param {object} properties - Properties to register
 */
export function updateSuperProperties(properties) {
  if (typeof window === 'undefined' || !posthogInitialized) {
    return;
  }

  try {
    posthog.register(properties);
    // console.log('[PostHog] Super properties updated:', properties);
  } catch (error) {
    console.error('[PostHog] Failed to update super properties:', error);
  }
}

/**
 * Update user properties
 * @param {object} properties - Properties to set
 */
export function updateUserProperties(properties) {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    // [TEMPORARILY DISABLED] Amplitude
    // if (amplitudeInitialized) {
    //   const identify = new amplitude.Identify();
    //   Object.entries(properties).forEach(([key, value]) => {
    //     identify.set(key, value);
    //   });
    //   amplitude.identify(identify);
    // }

    // PostHog - update person properties
    if (posthogInitialized) {
      posthog.setPersonProperties(properties);
      // console.log('[PostHog] Person properties updated:', properties);
    }
  } catch (error) {
    console.error('[Analytics] Failed to update user properties:', error);
  }
}

/**
 * Set user properties only if not already set (for first-time events)
 * @param {object} properties - Properties to set once
 */
export function setOnceUserProperties(properties) {
  if (typeof window === 'undefined' || !posthogInitialized) {
    return;
  }

  try {
    // PostHog setPersonPropertiesForFlags with $set_once
    posthog.setPersonPropertiesForFlags(properties, false);
    // Also use capture with $set_once for persistence
    posthog.capture('$set', {
      $set_once: properties,
    });
    // console.log('[PostHog] Person properties set once:', properties);
  } catch (error) {
    console.error('[Analytics] Failed to set once user properties:', error);
  }
}

/**
 * Increment user properties (for counters like total_articles_saved)
 * Uses localStorage to track counts client-side since PostHog JS SDK doesn't have native increment
 * @param {object} properties - Properties to increment { property: amount }
 */
export function incrementUserProperties(properties) {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    // [TEMPORARILY DISABLED] Amplitude
    // if (amplitudeInitialized) {
    //   const identify = new amplitude.Identify();
    //   Object.entries(properties).forEach(([key, value]) => {
    //     identify.add(key, value);
    //   });
    //   amplitude.identify(identify);
    // }

    // PostHog - use localStorage workaround (no native increment)
    if (posthogInitialized) {
      const STORAGE_KEY = 'reedle_user_counters';
      const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');

      // Increment each property
      const updated = { ...stored };
      Object.entries(properties).forEach(([key, amount]) => {
        updated[key] = (updated[key] || 0) + amount;
      });

      // Save back to localStorage
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));

      // Update PostHog with new totals
      posthog.setPersonProperties(updated);
      // console.log('[PostHog] User properties incremented:', properties, '→ totals:', updated);
    }
  } catch (error) {
    console.error('[Analytics] Failed to increment user properties:', error);
  }
}

/**
 * Get current user property counters from localStorage
 * @returns {object} Current counter values
 */
export function getUserCounters() {
  if (typeof window === 'undefined') {
    return {};
  }

  try {
    const STORAGE_KEY = 'reedle_user_counters';
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
  } catch (error) {
    console.error('[Analytics] Failed to get user counters:', error);
    return {};
  }
}

/**
 * Reset user counters (call on logout)
 */
export function resetUserCounters() {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    localStorage.removeItem('reedle_user_counters');
    // console.log('[Analytics] User counters reset');
  } catch (error) {
    console.error('[Analytics] Failed to reset user counters:', error);
  }
}

/**
 * Get PostHog instance for advanced usage
 * @returns {object|null} PostHog instance
 */
export function getPostHog() {
  return posthogInitialized ? posthog : null;
}

/**
 * React hook for PostHog feature flags.
 * Returns false until flags are loaded, then reflects the flag's value.
 * Safe to call even if PostHog is not initialized (always returns false).
 * @param {string} key - Feature flag key
 * @returns {boolean}
 */
export function useFeatureFlag(key) {
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    if (!posthogInitialized) return;
    // Check immediately in case flags are already loaded
    // eslint-disable-next-line react-hooks/set-state-in-effect -- syncing from the PostHog feature-flag SDK (external system); the subscribe path below is event-driven
    setEnabled(!!posthog.isFeatureEnabled(key));
    // Re-check when flags finish loading (async from server)
    const unsubscribe = posthog.onFeatureFlags(() => {
      setEnabled(!!posthog.isFeatureEnabled(key));
    });
    return () => { if (typeof unsubscribe === 'function') unsubscribe(); };
  }, [key]);

  return enabled;
}
